import {useRef, useState} from 'react';

import {BatchInvitationsFormValues} from '../components/BatchInvitationsModal.types';
import {
  ConfirmBatchInvitationsResponse,
  RowValidationError,
  ValidateBatchInvitationsResponse,
} from '../components/BatchInvitationsModal.types';
import {normalizeBatchInvitationErrorMessage} from '../utils/batchInvitationsErrorUtils';

import {useApiWrite} from '../../../../hooks/useApiWrite';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';
import {ApiResponseError} from '../../../../lib/api/v1/types';

interface Props {
  companyId: string;
  createdByUserId?: string | null;
  genericErrorMessage: string;
  confirmStateErrorMessage: string;
  malformedCsvErrorMessage: string;
  formatMalformedCsvLineErrorMessage: (line: string) => string;
  rowGenericErrorMessage: string;
  onConfirmSuccess: (batchId: string) => void;
}

export const useBatchInvitations = ({
  companyId,
  createdByUserId,
  genericErrorMessage,
  confirmStateErrorMessage,
  malformedCsvErrorMessage,
  formatMalformedCsvLineErrorMessage,
  rowGenericErrorMessage,
  onConfirmSuccess,
}: Props) => {
  const [validateSuccess, setValidateSuccess] = useState<boolean>(false);
  const [batchResourceId, setBatchResourceId] = useState<string | null>(null);
  const [validationSummary, setValidationSummary] = useState<{totalRowsCount?: number; validRowsCount?: number}>({});
  const [validationErrors, setValidationErrors] = useState<RowValidationError[]>([]);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const validationStateVersionRef = useRef(0);
  const activeValidationVersionRef = useRef<number | null>(null);
  const validationInFlightRef = useRef(false);

  const resetValidatedStateAndErrors = () => {
    validationStateVersionRef.current += 1;
    setValidateSuccess(false);
    setBatchResourceId(null);
    setValidationSummary({});
    setGeneralErrors([]);
    setValidationErrors([]);
  };

  const isCurrentValidationResponse = () => activeValidationVersionRef.current === validationStateVersionRef.current;

  const extractErrors = (error?: ApiResponseError) => {
    const errors = Array.isArray(error?.errors)
      ? error.errors.map((item: unknown) =>
        normalizeBatchInvitationErrorMessage(item, {
          genericErrorMessage,
          malformedCsvErrorMessage,
          formatMalformedCsvLineErrorMessage,
          confirmStateErrorMessage,
          rowGenericErrorMessage,
        })
      )
      : [
        normalizeBatchInvitationErrorMessage(error?.extra_details || error?.error_message || genericErrorMessage, {
          genericErrorMessage,
          malformedCsvErrorMessage,
          formatMalformedCsvLineErrorMessage,
          confirmStateErrorMessage,
          rowGenericErrorMessage,
        }),
      ].filter(Boolean);

    const rawValidationErrors = error?.validationErrors ?? error?.validation_errors;
    const rowErrors = Array.isArray(rawValidationErrors)
      ? rawValidationErrors
        .filter((item: any) => item && typeof item.row === 'number')
        .map((item: any) => ({
          row: item.row,
          errors: Array.isArray(item.errors)
            ? item.errors.map((entry: unknown) =>
              normalizeBatchInvitationErrorMessage(
                entry,
                {
                  genericErrorMessage,
                  malformedCsvErrorMessage,
                  formatMalformedCsvLineErrorMessage,
                  confirmStateErrorMessage,
                  rowGenericErrorMessage,
                },
                'row'
              )
            )
            : [rowGenericErrorMessage],
        }))
      : [];

    return {errors, rowErrors};
  };

  const {makeRequest: requestBatchValidation, isLoading: isValidating} = useApiWrite<
    Post.ValidateScreeningApplicantBatchInvitations,
    ValidateBatchInvitationsResponse
  >({
    method: 'post',
    path: PostRoutes[Post.ValidateScreeningApplicantBatchInvitations](companyId),
    disableLoadingOverlay: true,
    onSuccess: (response) => {
      validationInFlightRef.current = false;
      if (!isCurrentValidationResponse()) return;

      setValidateSuccess(true);
      setBatchResourceId(response.resourceId);
      setValidationSummary({
        totalRowsCount: response.totalRowsCount,
        validRowsCount: response.validRowsCount,
      });
      setGeneralErrors([]);
      setValidationErrors([]);
    },
    onError: (error) => {
      validationInFlightRef.current = false;
      if (!isCurrentValidationResponse()) return;

      const {errors, rowErrors} = extractErrors(error);
      setValidateSuccess(false);
      setBatchResourceId(null);
      setValidationSummary({});
      setGeneralErrors(errors);
      setValidationErrors(rowErrors);
    },
  });

  const {makeRequest: requestBatchConfirmation, isLoading: isConfirming} = useApiWrite<
    Post.ConfirmScreeningApplicantBatchInvitations,
    ConfirmBatchInvitationsResponse
  >({
    method: 'post',
    path: PostRoutes[Post.ConfirmScreeningApplicantBatchInvitations](companyId),
    disableLoadingOverlay: true,
    onSuccess: () => {
      if (!batchResourceId) return;
      onConfirmSuccess(batchResourceId);
    },
    onError: (error) => {
      const {errors} = extractErrors(error);
      setGeneralErrors(errors);
    },
  });

  const validateBatch = (values: BatchInvitationsFormValues) => {
    if (!values.file || validationInFlightRef.current) return;

    validationInFlightRef.current = true;
    activeValidationVersionRef.current = validationStateVersionRef.current;
    setValidateSuccess(false);
    setBatchResourceId(null);
    setValidationSummary({});
    setGeneralErrors([]);
    setValidationErrors([]);

    const formData = new FormData();
    formData.append('file', values.file);
    formData.append('screening_applicant_invitation_batch[job_posting]', values.jobPosting.trim());
    formData.append('screening_applicant_invitation_batch[location]', values.location.trim());
    if (createdByUserId) {
      formData.append('screening_applicant_invitation_batch[created_by_user_id]', createdByUserId);
    }
    if (values.departmentId) {
      formData.append('screening_applicant_invitation_batch[company_department_id]', values.departmentId);
    }

    requestBatchValidation(formData);
  };

  const confirmBatch = () => {
    if (isValidating || !validateSuccess || !batchResourceId) return;

    setGeneralErrors([]);
    requestBatchConfirmation({
      screeningApplicantInvitationBatchId: batchResourceId,
    });
  };

  return {
    validateSuccess,
    batchResourceId,
    validationSummary,
    validationErrors,
    generalErrors,
    isValidating,
    isConfirming,
    setGeneralErrors,
    resetValidatedStateAndErrors,
    validateBatch,
    confirmBatch,
  };
};
